import React, { useState, useRef, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import './App.css';

// Pages
import Home from './pages/Home';
import DrugSearch from './pages/DrugSearch';
import DrugDetail from './pages/DrugDetail';
import ClinicalTrials from './pages/ClinicalTrials';
import ClinicalTrialDetail from './pages/ClinicalTrialDetail';
import Chemicals from './pages/Chemicals';
import ChemicalDetail from './pages/ChemicalDetail';
import OrangeBook from './pages/OrangeBook';
import OrangeBookDetail from './pages/OrangeBookDetail';
import PubChem from './pages/PubChem';
import PubChemDetail from './pages/PubChemDetail';
import RxNorm from './pages/RxNorm';
import RxNormDetail from './pages/RxNormDetail';
import DailyMed from './pages/DailyMed';
import DailyMedDetail from './pages/DailyMedDetail';
import About from './pages/About';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import FAQ from './pages/FAQ';
import SavedSearches from './pages/SavedSearches';
import GDPR from './pages/GDPR';
import NewsDetail from './pages/NewsDetail';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';

function App() {
  const [menuState, setMenuState] = useState({
    tools: false,
    resources: false
  });

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState(null);

  const menuRefs = {
    tools: useRef(null),
    resources: useRef(null)
  };

  const toolsTimeoutRef = useRef(null);
  const resourcesTimeoutRef = useRef(null);

  const timeoutRefs = useMemo(() => ({
    tools: toolsTimeoutRef,
    resources: resourcesTimeoutRef
  }), []);

  const handleMenuEnter = (menuName) => {
    // Clear any existing timeout
    if (timeoutRefs[menuName].current) {
      clearTimeout(timeoutRefs[menuName].current);
      timeoutRefs[menuName].current = null;
    }

    setMenuState(prev => ({
      ...prev,
      [menuName]: true
    }));
  };

  const handleMenuLeave = (menuName) => {
    // Set a timeout to close the menu
    timeoutRefs[menuName].current = setTimeout(() => {
      setMenuState(prev => ({
        ...prev,
        [menuName]: false
      }));
    }, 200);
  };

  const handleMenuClick = (menuName) => {
    setMenuState(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(timeoutRefs).forEach(ref => {
        if (ref.current) {
          clearTimeout(ref.current);
        }
      });
    };
  }, [timeoutRefs]);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <div className="App">
        <nav className="navbar">
          <div className="nav-container">
            <Link to="/" className="nav-logo" onClick={() => setMobileMenuOpen(false)}>
              <span className="logo-icon">💊</span>
              <span className="logo-text">
                <span className="logo-main">PharmaDB</span>
                <span className="logo-sub">Research Platform</span>
              </span>
            </Link>

            <button 
              className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <div className={`nav-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              <Link to="/" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                <span className="nav-icon">🏠</span>
                <span>Home</span>
              </Link>

              <div className="nav-dropdown"
                ref={menuRefs.tools}
                onMouseEnter={() => !mobileMenuOpen && handleMenuEnter('tools')}
                onMouseLeave={() => !mobileMenuOpen && handleMenuLeave('tools')}
              >
                <button
                  className="nav-link dropdown-trigger"
                  onClick={() => mobileMenuOpen ? setMobileSubmenu(mobileSubmenu === 'tools' ? null : 'tools') : handleMenuClick('tools')}
                >
                  <span className="nav-icon">🔬</span>
                  <span>Search Tools</span>
                  <span className={`dropdown-arrow ${(menuState.tools || mobileSubmenu === 'tools') ? 'open' : ''}`}>▼</span>
                </button>
                {(menuState.tools || mobileSubmenu === 'tools') && (
                  <div className="dropdown-panel">
                    <div className="dropdown-section">
                      <div className="dropdown-label">FDA & Government</div>
                      <Link to="/drugs" className="dropdown-link" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>
                        <span className="link-icon">💊</span>
                        <div>
                          <div className="link-title">FDA Drugs</div>
                          <div className="link-desc">Drug approvals & safety</div>
                        </div>
                      </Link>
                      <Link to="/orangebook" className="dropdown-link" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>
                        <span className="link-icon">📖</span>
                        <div>
                          <div className="link-title">Orange Book</div>
                          <div className="link-desc">Therapeutic equivalence</div>
                        </div>
                      </Link>
                      <Link to="/dailymed" className="dropdown-link" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>
                        <span className="link-icon">📄</span>
                        <div>
                          <div className="link-title">DailyMed</div>
                          <div className="link-desc">Official drug labels</div>
                        </div>
                      </Link>
                    </div>
                    <div className="dropdown-section">
                      <div className="dropdown-label">Research Databases</div>
                      <Link to="/trials" className="dropdown-link" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>
                        <span className="link-icon">📋</span>
                        <div>
                          <div className="link-title">Clinical Trials</div>
                          <div className="link-desc">Study information</div>
                        </div>
                      </Link>
                      <Link to="/chemicals" className="dropdown-link" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>
                        <span className="link-icon">⚗️</span>
                        <div>
                          <div className="link-title">ChEBI & ChEMBL</div>
                          <div className="link-desc">Chemical entities</div>
                        </div>
                      </Link>
                      <Link to="/pubchem" className="dropdown-link" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>
                        <span className="link-icon">🧪</span>
                        <div>
                          <div className="link-title">PubChem</div>
                          <div className="link-desc">Chemical information</div>
                        </div>
                      </Link>
                      <Link to="/rxnorm" className="dropdown-link" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>
                        <span className="link-icon">💉</span>
                        <div>
                          <div className="link-title">RxNorm</div>
                          <div className="link-desc">Drug naming system</div>
                        </div>
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <Link to="/saved-searches" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                <span className="nav-icon">🔖</span>
                <span>Saved Searches</span>
              </Link>

              <div className="nav-dropdown"
                ref={menuRefs.resources}
                onMouseEnter={() => !mobileMenuOpen && handleMenuEnter('resources')}
                onMouseLeave={() => !mobileMenuOpen && handleMenuLeave('resources')}
              >
                <button
                  className="nav-link dropdown-trigger"
                  onClick={() => mobileMenuOpen ? setMobileSubmenu(mobileSubmenu === 'resources' ? null : 'resources') : handleMenuClick('resources')}
                >
                  <span className="nav-icon">ℹ️</span>
                  <span>Resources</span>
                  <span className={`dropdown-arrow ${(menuState.resources || mobileSubmenu === 'resources') ? 'open' : ''}`}>▼</span>
                </button>
                {(menuState.resources || mobileSubmenu === 'resources') && (
                  <div className="dropdown-panel">
                    <div className="dropdown-section">
                      <Link to="/about" className="dropdown-link" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>
                        <span className="link-icon">📚</span>
                        <div>
                          <div className="link-title">About</div>
                          <div className="link-desc">Learn about us</div>
                        </div>
                      </Link>
                      <Link to="/faq" className="dropdown-link" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>
                        <span className="link-icon">❓</span>
                        <div>
                          <div className="link-title">FAQ</div>
                          <div className="link-desc">Common questions</div>
                        </div>
                      </Link>
                      <Link to="/contact" className="dropdown-link" onClick={() => { setMobileMenuOpen(false); setMobileSubmenu(null); }}>
                        <span className="link-icon">✉️</span>
                        <div>
                          <div className="link-title">Contact</div>
                          <div className="link-desc">Get in touch</div>
                        </div>
                      </Link>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </nav>

        <main className="main-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/drugs" element={<DrugSearch />} />
            <Route path="/drug/:applicationNumber" element={<DrugDetail />} />
            <Route path="/trials" element={<ClinicalTrials />} />
            <Route path="/trial/:nctId" element={<ClinicalTrialDetail />} />
            <Route path="/chemicals" element={<Chemicals />} />
            <Route path="/chemical/:chemblId" element={<ChemicalDetail />} />
            <Route path="/orangebook" element={<OrangeBook />} />
            <Route path="/orangebook/:applicationNumber" element={<OrangeBookDetail />} />
            <Route path="/pubchem" element={<PubChem />} />
            <Route path="/pubchem/:cid" element={<PubChemDetail />} />
            <Route path="/rxnorm" element={<RxNorm />} />
            <Route path="/rxnorm/:rxcui" element={<RxNormDetail />} />
            <Route path="/dailymed" element={<DailyMed />} />
            <Route path="/dailymed/:setid" element={<DailyMedDetail />} />
            <Route path="/saved-searches" element={<SavedSearches />} />
            <Route path="/news/:id" element={<NewsDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/gdpr" element={<GDPR />} />
          </Routes>
        </main>

        <footer className="footer">
          <div className="footer-container">
            <div className="footer-section">
              <h4>💊 Pharmaceutical Database</h4>
              <p>Comprehensive pharmaceutical information from authoritative government sources.</p>
            </div>
            
            <div className="footer-section">
              <h4>Tools</h4>
              <ul>
                <li><Link to="/drugs">FDA Drugs</Link></li>
                <li><Link to="/trials">Clinical Trials</Link></li>
                <li><Link to="/chemicals">ChEMBL</Link></li>
                <li><Link to="/orangebook">Orange Book</Link></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>More Tools</h4>
              <ul>
                <li><Link to="/pubchem">PubChem</Link></li>
                <li><Link to="/rxnorm">RxNorm</Link></li>
                <li><Link to="/dailymed">DailyMed</Link></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Company</h4>
              <ul>
                <li><Link to="/about">About Us</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/contact">Contact</Link></li>
                <li><Link to="/saved-searches">Saved Searches</Link></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Legal</h4>
              <ul>
                <li><Link to="/privacy">Privacy Policy</Link></li>
                <li><Link to="/terms">Terms of Service</Link></li>
                <li><Link to="/gdpr">GDPR Compliance</Link></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 Pharmaceutical Database. All rights reserved.</p>
            <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>Data sources: FDA, NIH, NCBI, ClinicalTrials.gov, ChEBI, ChEMBL, PubChem, RxNorm, DailyMed</p>
          </div>
        </footer>

        <CookieConsent />
      </div>
    </Router>
  );
}

export default App;
